import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { FullSpinner } from "@/components/States";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

const fileUrl = (u) => u ? `${process.env.REACT_APP_BACKEND_URL}${u}?auth=${localStorage.getItem("mj_token")}` : "";
const TABS = ["Overview", "Profile", "Edit", "Photos", "Verification", "Engagement", "Subscription", "Safety", "Timeline", "Notes", "Audit"];
const EDIT_FIELDS = [
  ["full_name", "Full name"], ["height", "Height"], ["city", "City"], ["religion", "Religion"],
  ["community", "Community"], ["qualification", "Qualification"], ["occupation", "Occupation"],
  ["income_range", "Income range"], ["marital_status", "Marital status"], ["mother_tongue", "Mother tongue"],
  ["diet", "Diet"], ["family_type", "Family type"],
];

export default function AdminUser360() {
  const { uid } = useParams();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [note, setNote] = useState("");
  const [edit, setEdit] = useState({});
  const [editReason, setEditReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/admin/users/${uid}/360`).then(({ data }) => {
    setD(data);
    const p = data.profile || {};
    const seed = {}; EDIT_FIELDS.forEach(([k]) => { seed[k] = p[k] || ""; }); seed.about_me = p.about_me || "";
    setEdit(seed);
  }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [uid]);

  const doAction = async (action) => {
    const confirmNeeded = ["suspend", "restrict", "found_match", "force_logout", "revoke_sessions", "soft_delete"].includes(action);
    if (confirmNeeded && !window.confirm(`Confirm: ${action.replace(/_/g, " ")}?`)) return;
    const reason = ["suspend", "restrict", "soft_delete"].includes(action) ? (window.prompt("Reason (audited):", "") || "") : "";
    try { await api.post(`/admin/user-action/${uid}`, { action, reason }); toast.success("Done"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const requestInfo = async () => {
    try { const { data } = await api.post(`/admin/user-request-info/${uid}`, {}); toast.success(data.requested ? `Requested: ${data.fields.join(", ")}` : (data.message || "Nothing to request")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveEdit = async () => {
    setSaving(true);
    try { const { data } = await api.put(`/admin/users/${uid}/profile`, { fields: edit, reason: editReason }); toast.success(data.changed ? `${data.changed} field(s) updated` : "No changes"); setEditReason(""); load(); setTab("Profile"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/admin/user-notes/${uid}`, { text: note }); setNote(""); toast.success("Note added"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!d) return <FullSpinner label="Loading member…" />;
  const p = d.profile || {};
  const u = d.user || {};
  const badge = (t, cls) => <span className={`text-[11px] px-2 py-0.5 rounded-full ${cls}`}>{t}</span>;
  const Row = ({ k, v }) => <div className="flex justify-between gap-3 py-1 border-b border-[#781826]/15 text-sm"><span className="text-[#8A6D71]">{k}</span><span className="text-[#FAF5F0] text-right">{v || "—"}</span></div>;
  const pref = p.partner_preference || {};

  return (
    <div className="space-y-4" data-testid="admin-user-360">
      <Link to="/admin/users" className="inline-flex items-center gap-1 text-sm text-[#C59B27]"><ArrowLeft className="w-4 h-4" /> Users</Link>

      <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4 flex flex-wrap items-center gap-4">
        {p.photos?.[0]?.url ? <img src={fileUrl(p.photos[0].url)} alt="" className="w-16 h-16 rounded-full object-cover" /> :
          <div className="w-16 h-16 rounded-full bg-[#781826] flex items-center justify-center text-xl font-serif">{(p.full_name || "?")[0]}</div>}
        <div className="min-w-0">
          <div className="font-serif text-2xl font-bold">{p.full_name || "Unnamed"}</div>
          <div className="text-xs text-[#D8B4B9]">{p.profile_code} · {p.gender || "—"} · {p.age || "—"} · {p.city || "—"} · {p.occupation || "—"}</div>
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {badge(d.lifecycle, "bg-sky-900/40 text-sky-300")}
            {badge(u.status || "active", u.status === "active" ? "bg-emerald-900/40 text-emerald-300" : "bg-rose-900/40 text-rose-300")}
            {badge(u.subscription?.lifetime ? "lifetime" : (u.subscription?.plan || "free"), "bg-amber-900/40 text-amber-300")}
            {p.verification?.identity && badge("ID verified", "bg-emerald-900/40 text-emerald-300")}
            {badge(`${p.completion || 0}% complete`, "bg-[#1A090D] text-[#D8B4B9]")}
          </div>
        </div>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {u.status === "deleted" ? <button onClick={() => doAction("restore_deleted")} data-testid="u360-restore-deleted" className="text-xs px-3 py-1.5 rounded-lg border border-emerald-500/50 text-emerald-300">Restore account</button> : <>
          <button onClick={() => setTab("Edit")} data-testid="u360-edit" className="text-xs px-3 py-1.5 rounded-lg bg-[#C59B27] text-[#3B0A12] font-semibold">Edit</button>
          <button onClick={requestInfo} data-testid="u360-request-info" className="text-xs px-3 py-1.5 rounded-lg bg-[#1A090D] text-[#D8B4B9]">Request info</button>
          {u.status === "active" ? <button onClick={() => doAction("suspend")} data-testid="u360-suspend" className="text-xs px-3 py-1.5 rounded-lg border border-rose-500/50 text-rose-300">Suspend</button>
            : <button onClick={() => doAction("restore")} data-testid="u360-restore" className="text-xs px-3 py-1.5 rounded-lg border border-emerald-500/50 text-emerald-300">Restore</button>}
          <button onClick={() => doAction("restrict")} data-testid="u360-restrict" className="text-xs px-3 py-1.5 rounded-lg bg-[#1A090D] text-[#D8B4B9]">Restrict</button>
          <button onClick={() => doAction(p.visibility === "paused" ? "resume" : "pause")} className="text-xs px-3 py-1.5 rounded-lg bg-[#1A090D] text-[#D8B4B9]">{p.visibility === "paused" ? "Resume" : "Pause"}</button>
          <button onClick={() => doAction("revoke_sessions")} data-testid="u360-revoke" className="text-xs px-3 py-1.5 rounded-lg bg-[#1A090D] text-[#D8B4B9]">Revoke sessions</button>
          <button onClick={() => doAction("found_match")} className="text-xs px-3 py-1.5 rounded-lg bg-[#1A090D] text-[#D8B4B9]">Found match</button>
          <button onClick={() => doAction("soft_delete")} data-testid="u360-delete" className="text-xs px-3 py-1.5 rounded-lg border border-rose-500/50 text-rose-300">Delete</button>
          </>}
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {TABS.map((t) => <button key={t} onClick={() => setTab(t)} data-testid={`u360-tab-${t.toLowerCase()}`}
          className={`text-sm px-3 py-1.5 rounded-full whitespace-nowrap ${tab === t ? "bg-[#C59B27] text-[#3B0A12]" : "bg-[#2A0F14] text-[#D8B4B9]"}`}>{t}</button>)}
      </div>

      <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4">
        {tab === "Overview" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div><h3 className="text-xs uppercase text-[#C59B27] mb-1">Activity</h3>
              {Object.entries(d.engagement).map(([k, v]) => <Row key={k} k={k.replace(/_/g, " ")} v={v} />)}
            </div>
            <div><h3 className="text-xs uppercase text-[#C59B27] mb-1">Account</h3>
              <Row k="Email" v={d.security.email} /><Row k="Mobile" v={d.security.mobile} />
              <Row k="Email verified" v={d.security.email_verified ? "Yes" : "No"} />
              <Row k="Last active" v={d.security.last_active && new Date(d.security.last_active).toLocaleString()} />
              <Row k="Reports against" v={d.reports_against.length} />
              <Row k="Photos" v={`${d.photo_counts.approved} approved / ${d.photo_counts.pending} pending`} />
            </div>
          </div>
        )}
        {tab === "Profile" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div><h3 className="text-xs uppercase text-[#C59B27] mb-1">Basic & Career</h3>
              <Row k="Height" v={p.height} /><Row k="Marital status" v={p.marital_status} /><Row k="Mother tongue" v={p.mother_tongue} />
              <Row k="Religion" v={p.religion} /><Row k="Community" v={p.community} /><Row k="Qualification" v={p.qualification} />
              <Row k="Occupation" v={p.occupation} /><Row k="Income" v={p.income_range} /><Row k="Diet" v={p.diet} /><Row k="Family type" v={p.family_type} />
            </div>
            <div><h3 className="text-xs uppercase text-[#C59B27] mb-1">Partner preferences</h3>
              <Row k="Age" v={pref.age_min && `${pref.age_min}–${pref.age_max}`} /><Row k="Religion" v={pref.religion} />
              <Row k="Relocation" v={pref.relocation ? "Yes" : "—"} />
              <h3 className="text-xs uppercase text-[#C59B27] mt-3 mb-1">About</h3>
              <p className="text-sm text-[#D8B4B9]">{p.about_me || "—"}</p>
            </div>
          </div>
        )}
        {tab === "Edit" && (
          <div className="space-y-3" data-testid="u360-edit-panel">
            <p className="text-xs text-[#C59B27] uppercase">Edit profile (field changes are audited). Date of birth uses the DOB correction workflow.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {EDIT_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <label className="text-[11px] text-[#8A6D71] block mb-1">{label}</label>
                  <input value={edit[k] || ""} onChange={(e) => setEdit((s) => ({ ...s, [k]: e.target.value }))} data-testid={`u360-edit-${k}`}
                    className="w-full bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white" />
                </div>
              ))}
            </div>
            <div>
              <label className="text-[11px] text-[#8A6D71] block mb-1">About me</label>
              <textarea value={edit.about_me || ""} onChange={(e) => setEdit((s) => ({ ...s, about_me: e.target.value }))} rows={3} data-testid="u360-edit-about"
                className="w-full bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-[11px] text-[#8A6D71] block mb-1">Reason for change (audited)</label>
              <input value={editReason} onChange={(e) => setEditReason(e.target.value)} placeholder="e.g. Corrected per support ticket #123" data-testid="u360-edit-reason"
                className="w-full bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div className="flex gap-2">
              <button onClick={saveEdit} disabled={saving} data-testid="u360-edit-save" className="btn-gold px-5 py-2 rounded-lg text-sm disabled:opacity-60">{saving ? "Saving…" : "Save changes"}</button>
              <button onClick={() => setTab("Profile")} className="px-4 py-2 rounded-lg text-sm bg-[#1A090D] text-[#D8B4B9]">Cancel</button>
            </div>
          </div>
        )}
        {tab === "Photos" && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {(p.photos || []).length === 0 ? <p className="text-sm text-[#D8B4B9]">No photos.</p> : (p.photos || []).map((ph) => (
              <div key={ph.id} className="rounded-xl overflow-hidden bg-[#1A090D]">
                <img src={fileUrl(ph.url)} alt="" className="w-full h-28 object-cover" />
                <div className="text-[10px] text-center py-1 text-[#D8B4B9]">{ph.moderation || "approved"}{ph.private ? " · private" : ""}</div>
              </div>
            ))}
          </div>
        )}
        {tab === "Verification" && (
          <div className="space-y-2">
            {Object.entries(d.badges).map(([k, v]) => <Row key={k} k={k} v={v ? "Verified" : "Not verified"} />)}
            <h3 className="text-xs uppercase text-[#C59B27] mt-3 mb-1">Requests</h3>
            {d.verifications.length === 0 ? <p className="text-sm text-[#8A6D71]">No verification requests.</p> :
              d.verifications.map((v) => <Row key={v.id} k={`${v.type} (${new Date(v.created_at).toLocaleDateString()})`} v={v.status} />)}
          </div>
        )}
        {tab === "Engagement" && <div>{Object.entries(d.engagement).map(([k, v]) => <Row key={k} k={k.replace(/_/g, " ")} v={v} />)}</div>}
        {tab === "Subscription" && (
          <div>
            <Row k="Plan" v={u.subscription?.lifetime ? "Lifetime" : u.subscription?.plan} />
            <Row k="Status" v={u.subscription?.status} />
            <h3 className="text-xs uppercase text-[#C59B27] mt-3 mb-1">Payments</h3>
            {d.payments.length === 0 ? <p className="text-sm text-[#8A6D71]">No payments.</p> :
              d.payments.map((pm) => <Row key={pm.id} k={`${pm.plan} · ${new Date(pm.created_at).toLocaleDateString()}`} v={`₹${pm.amount} · ${pm.status}`} />)}
          </div>
        )}
        {tab === "Safety" && (
          <div>
            <h3 className="text-xs uppercase text-[#C59B27] mb-1">Reports against user ({d.reports_against.length})</h3>
            {d.reports_against.length === 0 ? <p className="text-sm text-[#8A6D71]">No reports.</p> :
              d.reports_against.map((r) => <Row key={r.id} k={`${r.reason} · ${new Date(r.created_at).toLocaleDateString()}`} v={r.status} />)}
            <Row k="Reports submitted by user" v={d.reports_by_count} />
          </div>
        )}
        {tab === "Timeline" && (
          <div className="space-y-2">
            {d.timeline.map((t, i) => (
              <div key={i} className="flex gap-3 text-sm"><span className="text-[#8A6D71] w-32 shrink-0">{new Date(t.at).toLocaleString()}</span><span className="text-[#FAF5F0]">{t.label}</span></div>
            ))}
          </div>
        )}
        {tab === "Notes" && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add internal note (staff only)" data-testid="u360-note-input"
                className="flex-1 bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white" />
              <button onClick={addNote} data-testid="u360-note-add" className="btn-gold px-4 rounded-lg text-sm">Add</button>
            </div>
            {d.notes.length === 0 ? <p className="text-sm text-[#8A6D71]">No notes.</p> :
              d.notes.map((n) => <div key={n.id} className="bg-[#1A090D] rounded-lg p-2 text-sm"><div className="text-[#FAF5F0]">{n.text}</div><div className="text-[11px] text-[#8A6D71]">{n.author} · {new Date(n.created_at).toLocaleString()}</div></div>)}
          </div>
        )}
        {tab === "Audit" && (
          <div className="space-y-1">
            {d.admin_actions.length === 0 ? <p className="text-sm text-[#8A6D71]">No admin actions recorded.</p> :
              d.admin_actions.map((a) => <div key={a.id} className="flex justify-between text-sm border-b border-[#781826]/15 py-1"><span className="text-[#FAF5F0]">{a.action}</span><span className="text-[#8A6D71]">{new Date(a.created_at).toLocaleString()}</span></div>)}
          </div>
        )}
      </div>
    </div>
  );
}
